// ───────────────────────────────────────────────────────────────────────────
// Client-side gate for BulkUploadPanel.
//
// A drag-and-drop bypasses the OS file dialog's `accept` filter entirely, so
// the extension / MIME / size rules here are what actually stop a .pdf or a
// .jpeg from reaching the importer. Kept in its own module (not in the panel)
// so it can be unit-tested and reused without pulling in React.
// ───────────────────────────────────────────────────────────────────────────

export const DEFAULT_ACCEPT = ['.xlsx', '.xls', '.csv'];
export const DEFAULT_MAX_SIZE_MB = 10;

// Extension → the MIME types a browser reports for it. Also fed to the file
// input's `accept` so the OS dialog greys out everything else up front.
export const MIME_BY_EXTENSION = {
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  '.xls': ['application/vnd.ms-excel'],
  '.csv': ['text/csv', 'application/csv', 'text/plain'],
};

// Friendlier rejection copy for the types people actually try by mistake —
// a saved-as-PDF invoice, or a phone photo of a printed sheet.
const REJECTION_HINTS = [
  { test: (ext, type) => ext === '.pdf' || type === 'application/pdf', label: 'PDF' },
  {
    test: (ext, type) =>
      type.startsWith('image/') ||
      ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp', '.svg'].includes(ext),
    label: 'Image',
  },
  { test: (ext) => ['.doc', '.docx'].includes(ext), label: 'Word document' },
  { test: (ext) => ['.zip', '.rar', '.7z'].includes(ext), label: 'Archive' },
  { test: (ext) => ['.txt', '.json', '.xml'].includes(ext), label: 'Plain text' },
];

export function getFileExtension(name = '') {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

export function formatFileSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// buildAcceptAttr returns the value for <input accept="…">: extensions AND
// their MIME types, because Safari and Firefox each honour only one of them.
export function buildAcceptAttr(accept = DEFAULT_ACCEPT) {
  const extensions = accept.map((item) => item.toLowerCase());
  return extensions.concat(extensions.flatMap((ext) => MIME_BY_EXTENSION[ext] ?? [])).join(',');
}

// validateBulkFile returns null when the file may be uploaded, or a
// user-facing reason string when it may not.
export function validateBulkFile(file, { accept = DEFAULT_ACCEPT, maxSizeMB = DEFAULT_MAX_SIZE_MB } = {}) {
  if (!file) return 'No file selected.';

  const extension = getFileExtension(file.name);
  const mimeType = (file.type || '').toLowerCase();
  const allowed = accept.map((item) => item.toLowerCase());
  const allowedLabel = allowed.join(', ');

  if (!extension) {
    return `"${file.name}" has no file extension. Only ${allowedLabel} files are allowed.`;
  }
  if (!allowed.includes(extension)) {
    const hint = REJECTION_HINTS.find((rule) => rule.test(extension, mimeType));
    return hint
      ? `${hint.label} files are not supported. Only ${allowedLabel} files are allowed.`
      : `"${extension}" files are not supported. Only ${allowedLabel} files are allowed.`;
  }
  // An extension can be faked by renaming report.pdf to report.xlsx, so
  // cross-check the MIME type the browser sniffed. An empty type is normal
  // (some OSes report nothing for .csv), so only a positively-wrong type is
  // rejected here.
  const expectedMimes = MIME_BY_EXTENSION[extension];
  if (mimeType && expectedMimes && !expectedMimes.includes(mimeType)) {
    return `"${file.name}" is not a real ${extension} file (detected ${mimeType}). Re-save it from Excel and try again.`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty. Fill in at least one row before uploading.`;
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    return `File is ${formatFileSize(file.size)}. The limit is ${maxSizeMB} MB.`;
  }
  return null;
}
